import React from 'react';
import { useEffect, useState } from 'react';
import { useFormV2 } from '@abyss/web/hooks/useForm';
import { useOverlay } from '@abyss/web/hooks/useOverlay';
import { AlertService } from '@src/api/common/AlertService';
import {
  GridDefaultColumns,
  IGridBuilderProps,
} from '@src/components/global/GridBuilder/interfaces/IGridBuilderProps';
import { AlertVariantType } from '@src/constants/AlertVariantType';
import { ApiStatus } from '@src/constants/ApiStatus';
import { ResponseService } from '@src/api/web-services/features/ResponseService';
import { RegionService } from '@src/api/web-services/features/RegionService/index';
import { PageTitleService } from '@src/api/common/PageTitleService';
import { BreadcrumbService } from '@src/api/common/BreadcrumbService';
import { Breadcrumbs, Title } from './PageInfo';
import { SURVEY_MODAL_ID } from './SurveyResponseModal';
import { UserService } from '@src/api/common/UserService';
import { ApiToInputDataTransformService } from '@src/api/common/ApiToInputDataTransformService';


export const ResponseGridController = () => {
   type Data = {
    regions: Array<any>;
  };

  const [pageData, setPageData] = useState<Data>({
    regions: [],
  });
  const { AlertModel, ShowAlert } = AlertService();
  const { GetResponseAll, GetResponseBySourceId } = ResponseService();
  const { GetAll: GetRegionAll } = RegionService();
  const { UpdatePageTitle } = PageTitleService();
  const { UpdateBreadcrumb } = BreadcrumbService();
  const surveyModalOverlay = useOverlay(SURVEY_MODAL_ID);
  const { GetSelectInputOptions } = ApiToInputDataTransformService();


  const [surveyAnswers, setSurveyAnswers] = useState<Record<string, string>>({
    question1: '',
    question2: '',
    question3: '',
    question4: '',
    question5: '',
  });

  const [surveySourceSessionId, setSurveySourceSessionId] = useState<
    number | string
  >('');

  const userData = UserService.GetUser();
  const isAdmin = userData?.allowedRoles?.includes('AZU_CTE_ADMIN') ?? false;
  const isViewer = userData?.allowedRoles?.includes('AZU_CTE_VIEWER') ?? false;

  const form = useFormV2({
    defaultValues: {
      recipientDisplayName: '',
      recipientDepartment: '',
      responseText: '',
      assignedTo: '',
      technology: '',
      theme: '',
      followUpStatus: '',
      action: '',
      actionOwner: '',
      recipientDivision: '',
      regionId: '',
      region: '',
    },
  });

  const DataSource = {
    LoadRegions: async () => {
      const data = await GetSelectInputOptions('id', 'regionName', GetRegionAll);
      if (data && data.data) {
        await setPageData((pageData) => ({
          ...pageData,
          regions: data.data,
        }));
        return data.data;
      }
      return [];
    },
    GridApiMethod: async (formObject: any = null) => {
      const formData =
        formObject !== null ? formObject.getValues() : form.getValues();
      let regions = pageData.regions;

      if ((!regions || regions.length === 0) && formData.regionId) {
        regions = await DataSource.LoadRegions();
      }

      const regionValue = formData.regionId;
      const regionId = regionValue && Number(regionValue) > 0 ? Number(regionValue) : 0;
      const regionName =
        regions.find((r) => Number(r.value) === regionId)?.label ??
        '';

      const body = {
        recipientDisplayName: formData.recipientDisplayName ?? '',
        recipientDepartment: formData.recipientDepartment ?? '',
        responseText: formData.responseText ?? '',
        assignedTo: formData.assignedTo ?? '',
        technology: formData.technology ?? '',
        theme: formData.theme ?? '',
        followUpStatus: formData.followUpStatus ?? '',
        action: formData.action ?? '',
        actionOwner: formData.actionOwner ?? '',
        recipientDivision: formData.recipientDivision ?? '',
        region: regionName ?? '',
      };
      const apiData = await GetResponseAll(body);
      if (apiData.status === ApiStatus.Success && apiData.data?.results) {
        return apiData.data.results.map((item: any) => ({
          ...item,
          number: item.sourceSessionId,
        }));
      }
      AlertModel.variant = AlertVariantType.ERROR;
      AlertModel.message = apiData.message
        ? apiData.message
        : apiData.data?.message;
      AlertModel.visible = true;
      AlertModel.isPermanent = true;
      await ShowAlert(AlertModel);
      return [];
    },
  };

  const gridProps: IGridBuilderProps = {
    gridName: 'responses-grid',
    columns: [],
    gridHeading: 'Responses',
    gridOptionName: 'responses-list-options',
    gridApiMethod: DataSource.GridApiMethod,
    form,
  };

  const Action = {
    OpenSurveyModal: async (sourceSessionId: number | string) => {
      const apiData = await GetResponseBySourceId({ sourceSessionId });
      if (apiData.status === ApiStatus.Success && apiData.data?.result) {
        const answers = (apiData.data.result.responseText ?? '')
          .split('|')
          .map((s: string) => s.trim());
        setSurveyAnswers({
          question1: answers[0] ?? '',
          question2: answers[1] ?? '',
          question3: answers[2] ?? '',
          question4: answers[3] ?? '',
          question5: answers[4] ?? '',
        });
        setSurveySourceSessionId(sourceSessionId);
      } else {
        setSurveyAnswers({
          question1: '',
          question2: '',
          question3: '',
          question4: '',
          question5: '',
        });
        setSurveySourceSessionId('');
        AlertModel.variant = AlertVariantType.ERROR;
        AlertModel.message =
          apiData.data?.message ?? 'Failed to load survey response.';
        AlertModel.visible = true;
        AlertModel.isPermanent = true;
        await ShowAlert(AlertModel);
      }
      surveyModalOverlay.open();
    },
    InIt: async () => {
      await UpdatePageTitle(Title);
      await UpdateBreadcrumb(Breadcrumbs);
    },
    Load: async () => {
      await DataSource.LoadRegions();
    },
    
  };

  useEffect(() => {
    Action.InIt();
    Action.Load();
  }, []);

  return {
    form,
    gridProps,
    surveyAnswers,
    surveySourceSessionId,
    onResponseClick: Action.OpenSurveyModal,
    isAdmin,
    isViewer,
    pageData,
  };
};
